import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { navLinks } from "@/utils/media";

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  const submitMobileSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/95 backdrop-blur-md">
      <div className="flex h-[68px] w-full items-center gap-4 px-4 md:px-8">
        <Link to="/" className="group flex shrink-0 items-center gap-2.5">
          <span className="relative flex h-10 w-10 items-center justify-center rounded-lg bg-primary-950 text-[15px] font-semibold tracking-tight text-background-50">
            日韩
            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-accent-500" />
          </span>
          <span className="hidden flex-col leading-none sm:flex">
            <span className="font-heading text-[15px] font-semibold tracking-tight text-foreground-950">
              日韩影视大全
            </span>
            <span className="mt-1 font-label text-[9px] uppercase tracking-[0.22em] text-foreground-500">
              Japan · Korea Cinema
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }) =>
                `cursor-pointer rounded-md px-3.5 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-primary-50 font-medium text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className="hidden h-10 w-44 items-center gap-2 rounded-full border border-background-300/70 bg-background-100 px-3.5 transition-colors focus-within:border-primary-400 focus-within:bg-background-50 md:flex xl:w-64"
          >
            <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              ref={inputRef}
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视、主演"
              aria-label="搜索影视作品"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </form>

          <Link
            to="/search"
            aria-label="搜索"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300/70 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 md:hidden"
          >
            <i className="ri-search-line text-lg" />
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={mobileOpen}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-primary-950 text-background-50 lg:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 top-[68px] z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-primary-950/40"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="rk-fade-in relative max-h-[calc(100vh-68px)] overflow-y-auto border-t border-background-200 bg-background-50 px-5 pb-8 pt-5">
            <form
              onSubmit={submitMobileSearch}
              className="flex h-11 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4"
            >
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视、主演、导演"
                aria-label="搜索影视作品"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button type="submit" className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-700">
                搜索
              </button>
            </form>

            <div className="mt-5 grid grid-cols-2 gap-2">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `flex cursor-pointer items-center justify-between rounded-lg border px-4 py-3 text-sm transition-colors ${
                      isActive
                        ? "border-primary-200 bg-primary-50 font-medium text-primary-700"
                        : "border-background-200 bg-background-100 text-foreground-800"
                    }`
                  }
                >
                  {link.label}
                  <i className="ri-arrow-right-s-line text-base text-foreground-400" />
                </NavLink>
              ))}
            </div>

            <div className="mt-6 rounded-lg bg-secondary-50 p-4">
              <p className="font-label text-[11px] uppercase tracking-[0.18em] text-secondary-700">本周焦点</p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                首页每周更新日韩新片与热播剧集，收藏与浏览记录会保存在你的浏览器里。
              </p>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}